// API service layer for RT Radiology Suite
import axios from 'axios';
import { getErrorMessage } from '../utils/errorHelpers';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Type definitions
export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: 'admin' | 'radiologist' | 'center_staff' | 'technician' | 'viewer';
  center_id: number;
  is_active: boolean;
  assigned_institutions: string[];
  disabled_pages: string[];
  last_login: string | null;
  created_at: string;
  updated_at: string;
  theme_color?: string;
  // Manage Viewers feature: admin-managed allow-list of external desktop DICOM
  // viewers this radiologist may use (subset of 'radiant'/'horos'/'miele_lxiv',
  // always [] for non-radiologists), and this radiologist's own self-service
  // per-modality viewer preference (modality_code -> viewer key; a modality with
  // no entry defaults to 'ohif').
  enabled_viewers?: string[];
  viewer_preferences?: Record<string, string>;
  // Sort Preference feature: default sort applied when opening Study Management,
  // Reading Module, or the Viewer. Secondary field/direction are optional
  // tie-breakers, used only when two rows are equal on the primary field --
  // default_sort_field_2 is null/absent when no tie-breaker is set.
  default_sort_field?: string;
  default_sort_direction?: 'asc' | 'desc';
  default_sort_field_2?: string | null;
  default_sort_direction_2?: 'asc' | 'desc' | null;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface ApiError {
  detail: string;
  status?: number;
}

// Auth storage: normal logins use localStorage (shared across tabs, persists across
// browser restarts, as before). A session opened via a Hub's embedded iframe
// (?hub_token=...) instead uses sessionStorage, which is isolated per-tab - localStorage
// is shared by every browsing context on an origin, so writing a hub-launched token there
// would silently clobber any other tab already logged into this same Suite domain.
// AuthContext sets the 'is_hub_session' flag before this module is ever asked for a token.
// Mirrors the backend's validate_password_strength (app/api/v1/users.py) so the
// UI can reject a weak password instantly instead of waiting on a round trip -
// keep both in sync if the policy ever changes. Returns an error string, or
// null if the password is acceptable.
export const validatePasswordStrength = (password: string): string | null => {
  if (password.length < 8) {
    return 'Password must be at least 8 characters long';
  }
  const categories = [
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^a-zA-Z0-9]/.test(password),
  ].filter(Boolean).length;
  if (categories < 3) {
    return 'Password must include at least 3 of: lowercase letters, uppercase letters, numbers, special characters';
  }
  return null;
};

export const authStorage = (): Storage =>
  sessionStorage.getItem('is_hub_session') === 'true' ? sessionStorage : localStorage;

// Wipes auth state from BOTH storages plus the hub-session flag in one atomic pass.
// Used at every place a session starts or ends so a tab can never be left with a
// token in one storage and the routing flag pointing at the other (the cause of the
// Hub logout/relogin -> intermittent "Could not validate credentials" race).
export const clearAuthStorage = (): void => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('is_hub_session');
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = authStorage().getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const failedConfig = error.config;
      const requestToken = (failedConfig?.headers?.Authorization as string | undefined)?.replace('Bearer ', '');
      const currentToken = authStorage().getItem('access_token');

      // The request that failed was dispatched under a token that's since been
      // replaced (e.g. a Hub logout/relogin race, or a background poll that
      // outlived the session that started it). The CURRENT session may still be
      // valid, so retry once under it instead of tearing down a good login over a
      // stale request's failure.
      if (requestToken && currentToken && requestToken !== currentToken && !failedConfig._retriedWithCurrentToken) {
        failedConfig._retriedWithCurrentToken = true;
        failedConfig.headers.Authorization = `Bearer ${currentToken}`;
        return api(failedConfig);
      }

      // Clear auth and redirect to login
      clearAuthStorage();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  login: async (username: string, password: string): Promise<LoginResponse> => {
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);
      
      const response = await api.post<LoginResponse>('/api/v1/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // Clear any leftover session (e.g. this tab was previously a Hub session)
      // before writing the new one, so storage and the is_hub_session flag can
      // never disagree.
      clearAuthStorage();
      authStorage().setItem('access_token', response.data.access_token);
      authStorage().setItem('user', JSON.stringify(response.data.user));

      return response.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Login failed');
      throw new Error(message);
    }
  },

  logout: () => {
    clearAuthStorage();
  },
  
  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await api.get<User>('/api/v1/auth/me');
      return response.data;
    } catch (error: any) {
      const message = getErrorMessage(error, 'Failed to get user info');
      throw new Error(message);
    }
  },
  
  isAuthenticated: (): boolean => {
    const token = authStorage().getItem('access_token');
    return !!token;
  },
};

// Studies endpoints
export const studiesAPI = {
  getAll: () => api.get('/api/v1/studies/'),
  getById: (id: string) => api.get(`/api/v1/studies/${id}`),
  create: (data: any) => api.post('/api/v1/studies/', data),
  update: (id: string, data: any) => api.put(`/api/v1/studies/${id}`, data),
  delete: (id: string) => api.delete(`/api/v1/studies/${id}`),
  // This deployment's own Orthanc AE Title -- needed to build RadiAnt/Horos/
  // Miele-LXIV deep links (Manage Viewers feature). Any authenticated user may
  // call this; never hardcode the AET client-side.
  getOrthancAet: () => api.get<{ aet: string }>('/api/v1/studies/orthanc-aet'),
  // Reused by StudyManagement/ReadingModule/ViewerModule modality filter buttons;
  // also reused here to populate the modality list on ViewerPreferences.tsx.
  getModalityCounts: () => api.get('/api/v1/studies/modality-counts'),
  // Distinct institution names for dropdowns (staff assignment, Download Excel).
  getInstitutions: () => api.get<string[]>('/api/v1/studies/institutions'),
  // Download Excel feature: per-institution, per-month study records export.
  // Longer timeout than the shared 10s default -- generating this walks every
  // matching study's Orthanc series data to compute the views count.
  exportExcel: (institutionName: string, month: string) =>
    api.get('/api/v1/studies/export-excel', {
      params: { institution_name: institutionName, month },
      responseType: 'blob',
      timeout: 60000,
    }),
};

// Reports endpoints
export const reportsAPI = {
  getAll: () => api.get('/api/v1/reports/'),
  getById: (id: string) => api.get(`/api/v1/reports/${id}`),
  getByStudy: (studyId: string) => api.get(`/api/v1/reports/study/${studyId}`),
  create: (data: any) => api.post('/api/v1/reports/', data),
  update: (id: string, data: any) => api.put(`/api/v1/reports/${id}`, data),
  delete: (id: string) => api.delete(`/api/v1/reports/${id}`),
};

export default api;

// User management endpoints
export const usersAPI = {
  updateProfile: (currentPassword: string, username: string, email: string, newPassword?: string) =>
    api.put('/api/v1/users/me', null, {
      params: {
        current_password: currentPassword,
        username,
        email,
        new_password: newPassword || undefined
      }
    }),

  updateThemeColor: (themeColor: string) =>
    api.put('/api/v1/users/me/theme-color', null, {
      params: { theme_color: themeColor }
    }),

  listUsers: () => api.get('/api/v1/users/'),
  
  createUser: (data: {
    username: string;
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role: string;
  }) => api.post('/api/v1/users/', data),
  
  updateUser: (userId: number, data: {
    username?: string;
    email?: string;
    password?: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    is_active?: boolean;
    assigned_institutions?: string[];
  }) => api.put(`/api/v1/users/${userId}`, data),
  
  deleteUser: (userId: number) => api.delete(`/api/v1/users/${userId}`),

  setPageAccess: (userId: number, pageKey: string, enabled: boolean) =>
    api.patch(`/api/v1/users/${userId}/pages/${pageKey}`, { enabled }),

  setViewerAccess: (userId: number, viewerKey: string, enabled: boolean) =>
    api.patch(`/api/v1/users/${userId}/viewers/${viewerKey}`, { enabled }),

  // Radiologist self-service viewer preferences (self-only, no user_id in the URL).
  setViewerPreference: (modalityCode: string, viewer: string) =>
    api.patch(`/api/v1/users/me/viewer-preferences/${encodeURIComponent(modalityCode)}`, { viewer }),
  clearViewerPreference: (modalityCode: string) =>
    api.delete(`/api/v1/users/me/viewer-preferences/${encodeURIComponent(modalityCode)}`),

  // Self-service default sort order (self-only, no user_id in the URL). Pass
  // null for sortField2/sortDirection2 to clear the secondary tie-breaker.
  updateSortPreference: (
    sortField: string,
    sortDirection: 'asc' | 'desc',
    sortField2: string | null,
    sortDirection2: 'asc' | 'desc' | null
  ) =>
    api.put('/api/v1/users/me/sort-preference', {
      sort_field: sortField,
      sort_direction: sortDirection,
      sort_field_2: sortField2,
      sort_direction_2: sortDirection2,
    }),
};

// Image Print settings endpoints
export const imagePrintAPI = {
  list: () => api.get('/api/v1/image-print-settings'),
  save: (modalityId: number, enabled: boolean, imagesPerPage: number) =>
    api.put('/api/v1/image-print-settings', { modality_id: modalityId, enabled, images_per_page: imagesPerPage }),
  listAutocontrast: () => api.get('/api/v1/image-print-settings/autocontrast'),
  saveAutocontrast: (institutionName: string, loPercentile: number, hiPercentile: number) =>
    api.put('/api/v1/image-print-settings/autocontrast', {
      institution_name: institutionName, lo_percentile: loPercentile, hi_percentile: hiPercentile,
    }),
};

// AI Report Assistant endpoints
export const aiAPI = {
  getProviders: () => api.get('/api/v1/ai/providers'),
  updateProvider: (providerKey: string, data: {
    api_key?: string;
    model_id?: string;
    is_enabled?: boolean;
    is_default?: boolean;
  }) => api.put('/api/v1/ai/providers/' + providerKey, data),

  getRadiologists: () => api.get('/api/v1/ai/radiologists'),
  updateRadiologist: (userId: number, data: {
    ai_enabled: boolean;
    preferred_provider_key?: string | null;
  }) => api.put('/api/v1/ai/radiologists/' + userId, data),

  getPrompts: (userId: number) => api.get('/api/v1/ai/prompts?user_id=' + userId),
  savePrompt: (data: {
    user_id: number;
    institution_name: string;
    modality_code: string;
    system_prompt: string;
  }) => api.put('/api/v1/ai/prompts', data),

  getInstitutions: () => api.get('/api/v1/ai/institutions'),
  getMyConfig: () => api.get('/api/v1/ai/my-config'),

  generate: (studyId: number, data: {
    findings: string;
    provider_key: string;
    model_id?: string;
  }) => api.post('/api/v1/ai/generate/' + studyId, data),

  getStatus: (studyId: number) => api.get('/api/v1/ai/status/' + studyId),
};
